import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, model, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ControlContainer, NgControl } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { I18NextPipe } from 'angular-i18next';
import { isEmpty, isEqual } from 'lodash';
import { TableCellDirective, TableComponent } from 'mbs-ui-kit';
import { ComputersFacade } from 'mbs-ui-shared@facades/computers.facade';
import { PagedResponse } from 'mbs-ui-shared@models';
import Computer, { AgentType, GetComputersParams } from 'mbs-ui-shared@models/Computer';
import { ComputerOsIconPipe } from 'mbs-ui-shared@utils/pipes/os-icon.pipe';
import { BehaviorSubject, catchError, combineLatest, EMPTY, finalize, map, Observable, tap } from 'rxjs';
import { PagedListSelectComponent } from './paged-list-select.component';

@UntilDestroy()
@Component({
  selector: 'mbs-computer-list-select',
  templateUrl: './computer-list-select.component.html',
  styles: [],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, TableComponent, TableCellDirective, ComputerOsIconPipe, I18NextPipe]
})
export class ComputerListSelectComponent extends PagedListSelectComponent<Computer> {
  controlContainer: ControlContainer;
  protected ngControl: NgControl;
  protected i18nPipe: I18NextPipe;
  private facade = inject(ComputersFacade);

  public filter = model<{ filter: GetComputersParams; initial: boolean }>(null);
  protected lastUsedFilter: GetComputersParams = undefined;

  public notSetResult = input<boolean>(false);
  public computersNotFound = input<string>('');
  public showComputerDetails = input<boolean>(false);

  protected bindLabel = 'name';
  protected bindValue = 'hid';

  private loadingByFilter$ = new BehaviorSubject<boolean>(false);
  private errorByFilter$ = new BehaviorSubject<boolean>(false);

  public readonly filterDataLoaded = signal(false);
  public readonly hasVisibleItems = computed(() => !!this.items().length);
  public readonly isLoading = computed(() => this.loading());

  constructor() {
    const controlContainer = inject(ControlContainer, { optional: true, host: true, skipSelf: true });
    const ngControl = inject(NgControl, { optional: true, self: true });
    const i18nPipe = inject(I18NextPipe);

    super();
    this.controlContainer = controlContainer;
    this.ngControl = ngControl;
    this.i18nPipe = i18nPipe;

    this.headerName.set(this.i18nPipe.transform('shared:common.computer_one', { format: 'title' }));
    toObservable(this.filter)
      .pipe(untilDestroyed(this))
      .subscribe((filter) => this.fetchData$.next({ reloadData: !filter?.initial }));
  }

  public getAgentVersion(computer: Computer): string {
    return Computer.getAgent(computer, AgentType.Backup)?.version;
  }

  protected getData$(loadMore = false): Observable<PagedResponse<Computer>> {
    const filter = this.filter()?.filter || null;

    if (isEqual(this.lastUsedFilter, filter)) {
      return EMPTY;
    }

    this.filterDataLoaded.set(false);
    this.lastUsedFilter = filter;

    if (isEmpty(filter)) {
      this.facade.loadData(false, loadMore);

      return combineLatest([this.facade.getData$(), this.facade.getDataTotal$]).pipe(
        map(([computers, total]) => ({
          data: this.mapComputers(computers),
          total
        }))
      );
    } else {
      let dataReceived = false;

      this.loadingByFilter$.next(true);

      return this.facade.getComputers(filter).pipe(
        finalize(() => {
          this.loadingByFilter$.next(false);
          this.filterDataLoaded.set(dataReceived);
        }),
        tap(() => (dataReceived = true)),
        tap((data) => !this.notSetResult() && this.facade.setComputersData(data.data)),
        map((data) => ({
          data: this.mapComputers(data?.data || []),
          total: data?.total || 0
        })),
        catchError(() => {
          this.errorByFilter$.next(true);
          return EMPTY;
        })
      );
    }
  }

  protected getByIds$(ids: string[]): Observable<Computer[]> {
    this.facade.loadComputersByHids({ hids: ids });

    return this.facade.getByHids$(ids).pipe(map((computers) => this.mapComputers(computers || [])));
  }

  protected get dataHasError$(): Observable<boolean> {
    return combineLatest([this.facade.getDataHasError$, this.errorByFilter$]).pipe(
      map(([hasError, hasErrorByFilter]) => hasError || hasErrorByFilter)
    );
  }

  protected get loadingData$(): Observable<boolean> {
    return combineLatest([this.facade.getDataLoading$, this.loadingByFilter$]).pipe(
      map(([loading, loadingByFilter]) => loading || loadingByFilter)
    );
  }

  protected get loadingById$(): Observable<boolean> {
    return this.facade.computerLoading$;
  }

  protected areItemsTheSame(selectedItem: Computer, item: Computer): boolean {
    return selectedItem.hid === item.hid;
  }

  protected trackBy(item: Computer) {
    return item?.hid;
  }

  private mapComputers(computers: Computer[]): Computer[] {
    return computers.map((computer) => ({ ...computer, name: Computer.getComputerName(computer) }));
  }
}
